import type {
  PatchDonorCancelRequest,
  PatchDonorDashboardUpdateRequest,
  PatchDonorReactivateRequest,
} from '@charitystack/payments-contracts';
import { createTRPCUntypedClient, httpLink } from '@trpc/client';

export type DashboardPatchDonorRequest =
  | Extract<PatchDonorCancelRequest, { requestOrigin: 'DASHBOARD' }>
  | Extract<PatchDonorReactivateRequest, { requestOrigin: 'DASHBOARD' }>
  | PatchDonorDashboardUpdateRequest;

export interface FormAndFundDataRequest {
  readonly queryStringParameters?: Readonly<Record<string, unknown>>;
  readonly [key: string]: unknown;
}

export interface PortalDashboardFacade {
  patchDonor(
    input: Readonly<{
      authorization: string;
      body: DashboardPatchDonorRequest;
    }>,
  ): Promise<Response>;
  postFormAndFundData(
    input: Readonly<{
      authorization: string;
      body: FormAndFundDataRequest;
    }>,
  ): Promise<Response>;
}

/**
 * Sole application binding. These requests intentionally preserve the
 * existing legacy REST URLs, methods, headers, bodies, and response handling.
 * Merely landing the typed wrapper moves no traffic to payments-main.
 */
export const portalClient = Object.freeze<PortalDashboardFacade>({
  patchDonor({ authorization, body }) {
    return globalThis.fetch(import.meta.env.VITE_APP_UPDATE_DONORS_URL, {
      method: 'PATCH',
      headers: {
        accept: '*/*',
        'content-type': 'application/json',
        Authorization: authorization,
      },
      body: JSON.stringify(body),
    });
  },

  postFormAndFundData({ authorization, body }) {
    return globalThis.fetch(import.meta.env.VITE_APP_GET_FORM_AND_FUND_DATA_URL, {
      method: 'POST',
      headers: {
        accept: '*/*',
        'content-type': 'application/json',
        Authorization: authorization,
      },
      body: JSON.stringify(body),
    });
  },
});

/**
 * Explicit dark transport for focused protocol proof only. Application code
 * does not construct or select it, and there is no fallback/shadow request.
 */
export function createPortalTrpcTransport(
  input: Readonly<{
    endpoint: string;
    authorization: string;
    request: typeof globalThis.fetch;
  }>,
) {
  const endpoint = input.endpoint.replace(/\/$/, '');
  if (!endpoint) throw new Error('A Portal tRPC endpoint is required');
  const client = createTRPCUntypedClient({
    links: [
      httpLink({
        url: endpoint,
        headers: {
          Authorization: input.authorization,
          'X-CS-Authorization': input.authorization,
        },
        fetch: input.request,
      }),
    ],
  });
  return Object.freeze({
    patchDonor(body: DashboardPatchDonorRequest) {
      return client.mutation('payments.portal.patchDonor', body);
    },
    formAndFundData(body: FormAndFundDataRequest) {
      return client.mutation('payments.portal.formAndFundData', body);
    },
  });
}
